/**
 * =============================================================================
 * Route table — **Application / UI composition (not a domain)**
 * =============================================================================
 * DDD note:
 * - Route configuration is **infrastructure for navigation**; it wires React
 *   components (mostly lazy) to URLs. Business rules (who may see what) stay
 *   in the auth bounded context (`authorizationSlice` + future policies).
 * - Feature screens should eventually live under `src/domains/<context>/ui/pages`
 *   and be imported here via their domain's **public** `index.ts` barrel.
 *
 * Current scope (intentionally minimal): **shell home + settings** so CRA
 * builds succeed while `Header` + `MainLayout` remain the visual contract.
 * =============================================================================
 */

import React from 'react';
import { RouteEnum } from './routeEnum';
import { AppRoute, RoutesByRole, DefaultRoutes } from './routes.types';
import { IconDashboard, IconClients, IconFaq, IconNotifications, IconAI, IconSettings } from '../components/Icons';
import { paths } from './paths';

/** Default landing route after sign-in (per role prefix). */
const DashboardPage = React.lazy(
  () => import('../../domains/dashboard/ui/pages/DashboardPage'),
);
const ClientsPage = React.lazy(
  () => import('../../domains/clients/ui/pages/ClientsPage'),
);
const CompanyStockOwnersPage = React.lazy(
  () => import('../../domains/clients/ui/pages/CompanyStockOwnersPage'),
);
const ClientSupportPage = React.lazy(
  () => import('../../domains/clients/ui/pages/ClientSupportPage'),
);
const AdminsPage = React.lazy(
  () => import('../../domains/admins/ui/pages/CompanyAdminsPage'),
);
const NotificationsPage = React.lazy(
  () => import('../../domains/notifications/ui/pages/NotificationsPage'),
);
const SettingsPage = React.lazy(
  () => import('../../domains/settings/ui/pages/SettingsPage'),
);
const InboxListPage = React.lazy(
  () => import('../../domains/inbox/ui/pages/InboxListPage'),
);
const InboxThreadPage = React.lazy(
  () => import('../../domains/inbox/ui/pages/InboxThreadPage'),
);
const FeedbackListPage = React.lazy(
  () => import('../../domains/feedback/ui/pages/FeedbackListPage'),
);
const AiPage = React.lazy(
  () => import('../../domains/ai/ui/pages/AiPage'),
);

const SignIn = React.lazy(() => import('../../authorization/SignIn/SignIn'));
const SignUp = React.lazy(() => import('../../authorization/sign-up'));
const ForgotPassword = React.lazy(
  () => import('../../authorization/ForgotPassword/ForgotPassword'),
);
const ResetPassword = React.lazy(
  () => import('../../authorization/ResetPassword/ResetPassword'),
);
const ClientSignIn = React.lazy(
  () => import('../../authorization/SignIn/SignIn'),
);

const ownerRoutes: AppRoute[] = [
  {
    element: DashboardPage,
    route: `/owner/dashboard`,
    pageTitle: 'dashboard.page-title',
    pathKey: 'dashboard',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: true,
    isActive: currentPath => currentPath.includes(`/owner/dashboard`),
    renderIcon: currentPath => (
      <IconDashboard isActive={currentPath.includes(`/owner/dashboard`)} />
    ),
  },
  {
    element: ClientsPage,
    route: `/owner/clients`,
    pageTitle: 'navigation.clients',
    pathKey: 'clients',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: true,
    isActive: currentPath =>
      currentPath === '/owner/clients' || currentPath.startsWith('/owner/clients?'),
    renderIcon: currentPath => (
      <IconClients
        isActive={
          currentPath === '/owner/clients' || currentPath.startsWith('/owner/clients?')
        }
      />
    ),
  },
  {
    element: CompanyStockOwnersPage,
    route: '/owner/company/:id',
    pageTitle: 'navigation.companyStockOwners',
    pathKey: 'companyStockOwners',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
    isActive: currentPath => currentPath.includes('/owner/company/'),
    renderIcon: currentPath => (
      <IconClients isActive={currentPath.includes('/owner/company/')} />
    ),
  },
  {
    element: ClientSupportPage,
    route: '/owner/client/:companyId',
    pageTitle: 'clients.supportPageTitle',
    pathKey: 'clientSupport',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
    isActive: currentPath => currentPath.includes('/owner/client/'),
    renderIcon: currentPath => (
      <IconClients isActive={currentPath.includes('/owner/client/')} />
    ),
  },
  {
    element: AdminsPage,
    route: '/owner/clients/:companyId/admins',
    pageTitle: 'admins.pageTitle',
    pathKey: 'admins',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
    isActive: currentPath => /\/owner\/clients\/[^/]+\/admins/.test(currentPath),
    renderIcon: currentPath => (
      <IconClients isActive={/\/owner\/clients\/[^/]+\/admins/.test(currentPath)} />
    ),
  },
  {
    element: InboxListPage,
    route: `/owner/ticket`,
    pageTitle: 'navigation.tickets',
    pathKey: 'ticket',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: true,
    isActive: currentPath => currentPath.includes(`/owner/ticket`),
    renderIcon: currentPath => (
      <IconNotifications isActive={currentPath.includes(`/owner/ticket`)} />
    ),
  },
  {
    element: FeedbackListPage,
    route: `/owner/feedback`,
    pageTitle: 'navigation.feedback',
    pathKey: 'feedback',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: true,
    isActive: currentPath =>
      currentPath.includes(`/owner/feedback`) &&
      !currentPath.includes('/owner/feedback/'),
    renderIcon: currentPath => (
      <IconFaq
        isActive={
          currentPath.includes(`/owner/feedback`) &&
          !currentPath.includes('/owner/feedback/')
        }
      />
    ),
  },
  {
    element: InboxThreadPage,
    route: `/owner/feedback/:id`,
    pageTitle: 'navigation.feedback',
    pathKey: 'feedbackThread',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
    isActive: currentPath => currentPath.includes('/owner/feedback/'),
    renderIcon: currentPath => (
      <IconFaq isActive={currentPath.includes('/owner/feedback/')} />
    ),
  },
  {
    element: AiPage,
    route: `/owner/ai`,
    pageTitle: 'navigation.ai',
    pathKey: 'ai',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: true,
    isActive: currentPath => currentPath.includes(`/owner/ai`),
    renderIcon: currentPath => (
      <IconAI isActive={currentPath.includes(`/owner/ai`)} />
    ),
  },
  {
    element: InboxThreadPage,
    route: `/owner/ticket/:id`,
    pageTitle: 'navigation.tickets',
    pathKey: 'ticketThread',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
    isActive: currentPath => currentPath.includes('/owner/ticket/'),
    renderIcon: currentPath => (
      <IconNotifications isActive={currentPath.includes('/owner/ticket/')} />
    ),
  },
  {
    element: NotificationsPage,
    route: `/owner/notifications`,
    pageTitle: 'navigation.notifications',
    pathKey: 'notifications',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
    isActive: currentPath => currentPath.includes(`/owner/notifications`),
    renderIcon: currentPath => (
      <IconNotifications isActive={currentPath.includes(`/owner/notifications`)} />
    ),
  },
  {
    element: SettingsPage,
    route: `/owner${paths.settings}`,
    pageTitle: 'settings.page-title',
    pathKey: 'settings',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
    isActive: currentPath => currentPath.includes(`/owner${paths.settings}`),
    renderIcon: currentPath => (
      <IconSettings isActive={currentPath.includes(`/owner${paths.settings}`)} />
    ),
  },
  {
    element: SettingsPage,
    route: `/owner${paths.settingsTerms}`,
    pageTitle: 'settings.page-title',
    pathKey: 'settingsTerms',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
    isActive: currentPath =>
      currentPath.includes(`/owner${paths.settingsTerms}`),
    renderIcon: currentPath => (
      <IconSettings
        isActive={currentPath.includes(`/owner${paths.settingsTerms}`)}
      />
    ),
  },
  {
    element: SettingsPage,
    route: `/owner${paths.settingsProfile}`,
    pageTitle: 'settings.page-title',
    pathKey: 'settingsProfile',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
    isActive: currentPath =>
      currentPath.includes(`/owner${paths.settingsProfile}`),
    renderIcon: currentPath => (
      <IconSettings
        isActive={currentPath.includes(`/owner${paths.settingsProfile}`)}
      />
    ),
  },
  {
    element: SettingsPage,
    route: `/owner${paths.settingsAdmin}`,
    pageTitle: 'settings.page-title',
    pathKey: 'settingsAdmin',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
    isActive: currentPath => currentPath.includes(`/owner${paths.settingsAdmin}`),
    renderIcon: currentPath => (
      <IconSettings isActive={currentPath.includes(`/owner${paths.settingsAdmin}`)} />
    ),
  },
  {
    element: SettingsPage,
    route: `/owner${paths.settingsIntegrations}`,
    pageTitle: 'settings.page-title',
    pathKey: 'settingsIntegrations',
    roles: ['owner'],
    requireAuth: true,
    isInNavigation: false,
    isActive: currentPath =>
      currentPath.includes(`/owner${paths.settingsIntegrations}`),
    renderIcon: currentPath => (
      <IconSettings
        isActive={currentPath.includes(`/owner${paths.settingsIntegrations}`)}
      />
    ),
  },
];

export const defaultRoutesByRole: DefaultRoutes = {
  client: `/owner/dashboard`,
  tenant: `/owner/dashboard`,
  owner: `/owner/dashboard`,
  none: RouteEnum.SIGN_IN,
};

export const routesByRole: RoutesByRole = {
  owner: ownerRoutes,
  tenant: ownerRoutes,
  client: ownerRoutes,
  none: [],
};

/** Routes that do not require `MainLayout` (auth screens). */
export const publicRoutes: AppRoute[] = [
  { element: SignIn, route: RouteEnum.SIGN_IN, requireAuth: false },
  { element: SignUp, route: RouteEnum.SIGN_UP, requireAuth: false },
  {
    element: ForgotPassword,
    route: RouteEnum.FORGOT_PASSWORD,
    requireAuth: false,
  },
  { element: ResetPassword, route: RouteEnum.RESET_PASSWORD, requireAuth: false },
  {
    element: ClientSignIn,
    route: RouteEnum.CLIENT_SIGN_IN,
    requireAuth: false,
  },
];
